import React, { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError, API, getAuthToken } from "@/lib/api";
import { toast } from "sonner";
import {
  Search,
  Filter,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
} from "lucide-react";

/**
 * v1.2 PR-B.1 — Cross-org privacy acceptances viewer.
 *
 * Backend: GET /api/super-admin/privacy-acceptances?org_id&document_type&document_version&from_date&to_date&limit&offset
 *   - Returns array body, pagination via X-Total-Count response header.
 *
 * CSV export: GET /api/super-admin/privacy-acceptances/export.csv?...filters (50k cap).
 *
 * Note: raw IP is NEVER stored — `ip_hash` is shown instead. This is enforced
 * server-side (Phase 5 T5 fix) and is the privacy contract advertised to data
 * subjects.
 */
const PAGE_SIZE = 50;
const DOC_TYPES = ["NDA", "HealthSafety", "Privacy"];

export default function SuperAdminPrivacyAcceptances() {
  const [orgs, setOrgs] = useState([]);
  const [filters, setFilters] = useState({
    org_id: "", document_type: "", document_version: "",
    from_date: "", to_date: "",
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    api.get("/super-admin/orgs").then(({ data }) => setOrgs(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/super-admin/privacy-acceptances", { params });
      setRows(res.data || []);
      const t = parseInt(res.headers?.["x-total-count"] || "0", 10);
      setTotal(Number.isFinite(t) ? t : 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filters, offset]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const url = `${API}/super-admin/privacy-acceptances/export.csv?${qs.toString()}`;
    const token = getAuthToken();
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `privacy-acceptances-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("CSV exported");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <SuperAdminLayout>
      <div data-testid="super-admin-privacy-acceptances-page" className="space-y-5">
        <header className="flex items-end justify-between gap-4">
          <div>
            <div className="eb-overline mb-1">Super admin</div>
            <h1 className="text-3xl font-semibold text-navy-900 dark:text-white inline-flex items-center gap-2">
              <ClipboardCheck className="w-7 h-7 text-orange-500" /> Privacy acceptances (cross-org)
            </h1>
            <p className="text-sm text-navy-500 dark:text-navy-300 mt-1 max-w-3xl">
              Visitor &amp; contractor consent acceptances across every organisation. Raw IP is
              never stored — only its <code className="px-1 rounded bg-navy-50">ip_hash</code> for
              fraud-detection purposes.
            </p>
          </div>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-orange-500 text-white hover:bg-orange-600"
            data-testid="privacy-export-csv"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV ({total.toLocaleString()})
          </button>
        </header>

        <FilterBar
          orgs={orgs}
          filters={filters}
          onChange={(next) => { setOffset(0); setFilters({ ...filters, ...next }); }}
          onClear={() => { setOffset(0); setFilters({ org_id: "", document_type: "", document_version: "", from_date: "", to_date: "" }); }}
        />

        <Table
          rows={rows} loading={loading} total={total} offset={offset}
          onPage={setOffset} onRowClick={setDrawer}
        />
      </div>
      <Drawer row={drawer} onClose={() => setDrawer(null)} />
    </SuperAdminLayout>
  );
}

function FilterBar({ orgs, filters, onChange, onClear }) {
  const [local, setLocal] = useState(filters);
  useEffect(() => setLocal(filters), [filters]);
  const submit = (e) => { e?.preventDefault(); onChange(local); };
  return (
    <form
      onSubmit={submit}
      className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl p-4 grid gap-3 md:grid-cols-6 items-end"
      data-testid="privacy-filter-bar"
    >
      <div>
        <label className="eb-label">Organisation</label>
        <select className="eb-input" value={local.org_id}
                onChange={(e) => setLocal({ ...local, org_id: e.target.value })}
                data-testid="privacy-filter-org">
          <option value="">All</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div>
        <label className="eb-label">Document type</label>
        <select className="eb-input" value={local.document_type}
                onChange={(e) => setLocal({ ...local, document_type: e.target.value })}
                data-testid="privacy-filter-doc-type">
          <option value="">All</option>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label className="eb-label">Version</label>
        <input className="eb-input" placeholder="e.g. 1"
               value={local.document_version}
               onChange={(e) => setLocal({ ...local, document_version: e.target.value })}
               data-testid="privacy-filter-version" />
      </div>
      <div>
        <label className="eb-label">From date</label>
        <input type="date" className="eb-input" value={local.from_date}
               onChange={(e) => setLocal({ ...local, from_date: e.target.value })}
               data-testid="privacy-filter-from" />
      </div>
      <div>
        <label className="eb-label">To date</label>
        <input type="date" className="eb-input" value={local.to_date}
               onChange={(e) => setLocal({ ...local, to_date: e.target.value })}
               data-testid="privacy-filter-to" />
      </div>
      <div className="md:col-span-6 flex justify-end gap-2">
        <button type="button" onClick={onClear}
                className="text-xs px-3 py-1.5 rounded-md border border-navy-200 hover:bg-navy-50"
                data-testid="privacy-filter-clear">Clear</button>
        <button type="submit"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-navy-900 text-white hover:bg-navy-800"
                data-testid="privacy-filter-apply">
          <Filter className="w-3.5 h-3.5" /> Apply filters
        </button>
      </div>
    </form>
  );
}

function Table({ rows, loading, total, offset, onPage, onRowClick }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const current = Math.floor(offset / PAGE_SIZE) + 1;
  return (
    <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="privacy-table">
          <thead className="bg-navy-50/60 dark:bg-navy-900/40 text-xs text-navy-600 dark:text-navy-300 uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Org</th>
              <th className="text-left px-4 py-2">Identifier</th>
              <th className="text-left px-4 py-2">Document</th>
              <th className="text-left px-4 py-2">Version</th>
              <th className="text-left px-4 py-2">IP hash</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-100">
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-navy-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-navy-400">No acceptances match.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} onClick={() => onRowClick(r)}
                  className="cursor-pointer hover:bg-navy-50/40 transition-colors"
                  data-testid="privacy-row">
                <td className="px-4 py-2 text-xs font-mono text-navy-700 whitespace-nowrap">
                  {r.accepted_at ? new Date(r.accepted_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-2 text-xs text-navy-700 truncate max-w-[180px]">
                  {r.org_id ? r.org_id.slice(0, 8) + "…" : <span className="text-navy-300">(global)</span>}
                </td>
                <td className="px-4 py-2 text-xs text-navy-700 truncate max-w-[200px]">
                  {r.identifier_lower || <span className="text-navy-400">—</span>}
                </td>
                <td className="px-4 py-2">
                  <span className="text-xs font-medium text-navy-800">{r.document_type || "—"}</span>
                </td>
                <td className="px-4 py-2 text-xs font-mono text-navy-700">{r.version ?? "—"}</td>
                <td className="px-4 py-2 text-xs font-mono text-navy-500 truncate max-w-[160px]">
                  {r.ip_hash ? r.ip_hash.slice(0, 12) + "…" : <span className="text-navy-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <footer className="px-4 py-3 border-t border-navy-100 flex items-center justify-between text-xs text-navy-500" data-testid="privacy-pagination">
        <span>Page {current} of {pages} · {total.toLocaleString()} rows</span>
        <div className="inline-flex gap-1">
          <button disabled={offset <= 0} onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}
                  className="px-2 py-1 rounded border border-navy-200 hover:bg-navy-50 disabled:opacity-40"
                  data-testid="privacy-page-prev">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button disabled={offset + PAGE_SIZE >= total} onClick={() => onPage(offset + PAGE_SIZE)}
                  className="px-2 py-1 rounded border border-navy-200 hover:bg-navy-50 disabled:opacity-40"
                  data-testid="privacy-page-next">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </footer>
    </div>
  );
}

function Drawer({ row, onClose }) {
  if (!row) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="privacy-drawer">
      <button onClick={onClose} className="flex-1 bg-black/40" aria-label="Close drawer" />
      <aside className="w-full md:w-[520px] bg-white shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-white px-5 py-4 border-b border-navy-100 flex items-center justify-between">
          <div>
            <div className="eb-overline">Privacy acceptance</div>
            <h2 className="text-sm font-mono text-navy-900">{row.document_type || "—"} v{row.version ?? "—"}</h2>
          </div>
          <button onClick={onClose} className="text-navy-400 hover:text-navy-700">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div><div className="eb-overline mb-0.5">When</div><div className="text-navy-900 text-sm">{row.accepted_at ? new Date(row.accepted_at).toLocaleString() : "—"}</div></div>
          <div><div className="eb-overline mb-0.5">Organisation</div><div className="font-mono text-xs text-navy-900 break-all">{row.org_id || "(global)"}</div></div>
          <div><div className="eb-overline mb-0.5">Identifier (lower)</div><div className="font-mono text-xs text-navy-900 break-all">{row.identifier_lower || "—"}</div></div>
          <div><div className="eb-overline mb-0.5">IP hash</div><div className="font-mono text-xs text-navy-900 break-all">{row.ip_hash || "—"}</div></div>
          <div><div className="eb-overline mb-0.5">User agent</div><div className="font-mono text-xs text-navy-700 break-words">{row.user_agent || "—"}</div></div>
        </div>
      </aside>
    </div>
  );
}
